// ============================================================================
// folhaContasPagar — lançamentos que a Folha grava em Contas a Pagar (06/10/2026)
// ============================================================================
// Fase 0 do financeiro (contenção). Ao marcar uma linha da folha como PAGA, a
// Folha grava a saída em cash_movements; ao voltar pra PENDENTE, retira. Antes:
//   · procurava o que apagar só no CACHE do navegador (janela de 90 dias do
//     DataContext) → lançamento fora da janela ficava e duplicava;
//   · duplo clique gravava 2× (jul/26: a mesma tag 2× no Itaim e na Vila);
//   · apagava engolindo o erro e marcava 'paga' mesmo se a gravação falhasse;
//   · apagava até lançamento que alguém tinha corrigido à mão;
//   · rateava líquido/N em float (centavo sobrando/faltando), em categoria
//     fora da taxonomia ('Folha de Pagamento'), sem competência e em UTC.
// Aqui ficam as regras (testadas em tests/folha-contas-pagar.test.mjs): tag,
// rateio, montagem, consulta ao BANCO e a ordem de gravação com aborto. O
// FolhaPagamentoModule só trava o botão, mostra o aviso e cuida do recibo.
// Nada aqui mexe no cálculo da folha nem em /api/folha-recibos.
//
// FASE 1b (06/10/2026): o "marcar paga" passou para o SERVIDOR (/api/fin
// op=folha-pagar — fotografia imutável + 1 título por CANAL pela origem_key,
// numa transação). A tela agora usa só o bloco "Fase 1b" no fim deste
// arquivo (montar o pedido em centavos, tratar CONFLITOS e o "reabrir com
// motivo"). O caminho antigo (alternarPagamentoFolha e cia.) fica aqui só
// como referência/testes — a tela não grava mais em cash_movements.
// ============================================================================

import { SUPABASE_URL, restFetchAll } from './secureRest.js';

// Código da taxonomia de saídas (src/utils/expenseCategories.js) — não mais o
// rótulo livre 'Folha de Pagamento'.
export const FOLHA_CATEGORIA = 'folha_salarios';

const pad2 = (n) => String(n).padStart(2, '0');
const semPontoFinal = (m) => String(m || '').replace(/\.+\s*$/, '');
const errMsg = (err) => semPontoFinal(err?.message || String(err));

// Tag gravada na description: identifica os lançamentos que a Folha gerou pra
// UM funcionário (payroll_employees.id) num mês de PAGAMENTO. Formato mantido
// igual ao antigo pra achar também o que já foi gravado antes.
export function folhaTag(empId, year, month) {
  return `[FOLHA-${empId}-${year}-${pad2(month)}]`;
}

// Reais (float) → centavos inteiros. O toFixed corrige o ruído do float
// (1234.565 * 100 = 123456.49999…) antes de arredondar.
export function paraCentavos(valor) {
  const n = Number(valor);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Number((n * 100).toFixed(6)));
}

// Rateio em centavos pelo MAIOR RESTO: a soma das partes fecha exatamente com
// o total. Empate de resto → quem vem primeiro na lista leva o centavo.
// Ex.: 200000 ÷ [1,1,1] → [66667, 66667, 66666].
export function ratearCentavos(totalCents, pesos) {
  const n = Array.isArray(pesos) ? pesos.length : 0;
  if (!n) return [];
  const total = Math.trunc(Number(totalCents) || 0);
  const sinal = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  let ws = pesos.map(p => Math.max(0, Number(p) || 0));
  let soma = ws.reduce((a, b) => a + b, 0);
  if (soma <= 0) { ws = ws.map(() => 1); soma = n; }
  const partes = ws.map((w, i) => {
    const exato = (abs * w) / soma;
    const base = Math.floor(exato);
    return { i, base, resto: exato - base };
  });
  let sobra = abs - partes.reduce((a, p) => a + p.base, 0);
  const ordem = [...partes].sort((a, b) => (b.resto - a.resto) || (a.i - b.i));
  for (let k = 0; sobra > 0; k = (k + 1) % n, sobra--) ordem[k].base += 1;
  return partes.map(p => (p.base === 0 ? 0 : sinal * p.base));
}

// Linha "intacta" = ninguém mexeu depois que a Folha gravou: updated_at igual
// ao created_at (os dois nascem com o mesmo now() do insert; toda edição pela
// tela grava updated_at novo). Sem as duas datas → NÃO é intacta (na dúvida,
// não se apaga).
export function linhaIntacta(m, toleranciaMs = 2000) {
  const c = Date.parse(m?.created_at ?? m?.createdAt ?? '');
  const u = Date.parse(m?.updated_at ?? m?.updatedAt ?? '');
  if (!Number.isFinite(c) || !Number.isFinite(u)) return false;
  return Math.abs(u - c) <= toleranciaMs;
}

// Separa os lançamentos da tag em intactos (a Folha pode apagar/regerar) e
// editados à mão (NUNCA apagados por aqui).
export function separarLancamentos(lista, tag) {
  const daTag = (Array.isArray(lista) ? lista : []).filter(m => String(m?.description || '').includes(tag));
  return {
    intactas: daTag.filter(m => linhaIntacta(m)),
    editadas: daTag.filter(m => !linhaIntacta(m)),
  };
}

// Monta os lançamentos de "folha paga" (1 por unidade de destino), com o
// líquido rateado em centavos. Partes de 0 centavo não viram linha.
//   year/month       = mês do PAGAMENTO (tela da folha)
//   refYear/refMonth = COMPETÊNCIA (mês anterior — a folha de junho refere a maio)
//   hoje             = dia do pagamento, YYYY-MM-DD no fuso de SP (hojeSP)
export function montarLancamentosFolha({ tag, liquido, unitIds, nome, year, month, refYear, refMonth, hoje }) {
  const destinos = Array.isArray(unitIds) ? unitIds : [];
  if (!destinos.length) return [];
  const isShare = destinos.length > 1;
  const partes = ratearCentavos(paraCentavos(liquido), destinos.map(() => 1));
  const lastDay = new Date(year, month, 0).getDate();
  const vencimento = `${year}-${pad2(month)}-${pad2(lastDay)}`;
  const competencia = `${refYear}-${pad2(refMonth)}-01`;
  const mesLabel = new Date(year, month - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  const out = [];
  destinos.forEach((unitId, i) => {
    const cents = partes[i];
    if (!(cents > 0)) return;
    const valor = cents / 100;
    out.push({
      unitId,
      type: 'saida',
      category: FOLHA_CATEGORIA,
      amount: valor,
      date: hoje,
      method: 'PIX',
      description: `${tag} ${isShare ? `(rateio ${destinos.length} unidades)` : ''}`.trim(),
      titulo: `Folha ${mesLabel} — ${nome}`,
      fornecedor: nome,
      dataVencimento: vencimento,
      dataCompetencia: competencia,
      dataPagamento: hoje,
      formaPagamento: 'PIX',
      status: 'pago',
      valorPago: valor,
    });
  });
  return out;
}

// Lançamentos da tag direto do BANCO (não do cache do DataContext). REGRA
// LINEAR: falha de rede/permissão LANÇA — quem chama aborta, nunca segue como
// se não houvesse nada (era assim que a folha duplicava).
export async function buscarLancamentosFolha(tag) {
  const sel = 'id,unit_id,type,category,amount,date,description,titulo,status,created_at,updated_at';
  const filtro = encodeURIComponent(`*${tag}*`);
  const rows = await restFetchAll(
    `${SUPABASE_URL}/rest/v1/cash_movements?select=${sel}&description=like.${filtro}&order=created_at.asc`,
  );
  if (!Array.isArray(rows)) throw new Error('resposta inesperada ao consultar os lançamentos da folha');
  // Confere a tag de novo aqui: no LIKE do Postgres o "_" é curinga.
  return rows.filter(m => String(m?.description || '').includes(tag));
}

// Trava por chave (1 operação por linha da folha por vez). Síncrona: dois
// cliques no mesmo tick — antes de o React re-renderizar o botão travado —
// também caem aqui.
export function criarTravaPorChave() {
  const ativos = new Set();
  return {
    ocupada: (k) => ativos.has(k),
    async executar(k, fn) {
      if (ativos.has(k)) return { ok: false, ignorado: true };
      ativos.add(k);
      try { return await fn(); } finally { ativos.delete(k); }
    },
  };
}

const descreverPadrao = (m) => `• ${m?.titulo || 'Folha'} — ${m?.unit_id || 'sem unidade'} — R$ ${Number(m?.amount || 0).toFixed(2)} — ${m?.date || 'sem data'}`;

// Marca/desmarca a folha de UMA pessoa em Contas a Pagar e só então o status.
// Ordem (lançamentos primeiro, status por último): falha no meio nunca deixa
// "paga" sem lançamento, e clicar de novo termina o que faltou — o que a Folha
// gerou e ninguém mexeu é sempre SUBSTITUÍDO, nunca somado.
//   next          'paga' | 'pendente'
//   montarNovos   () => lançamentos a gerar, 1 por unidade (chamado no 'paga')
//   buscar        (tag) => lançamentos da tag no BANCO (lança em falha)
//   remover       (id) => exclui (lança em falha — nunca engolido)
//   gravar        (mov) => linha gravada com id (lança em falha)
//   salvarStatus  () => true se o payroll_record foi salvo
//   confirmar     (msg) => boolean (lançamento editado à mão: a pessoa decide)
//   nomeUnidade   (unitId) => nome pra pergunta (opcional)
// Devolve { ok: true, criados, apagados, editadosMantidos }
//      ou { ok: false, cancelado: true }   (a pessoa desistiu — nada mudou)
//      ou { ok: false, aviso }             (abortou — status NÃO mudou)
export async function alternarPagamentoFolha({
  next, tag, nome = 'funcionário', montarNovos, buscar = buscarLancamentosFolha,
  remover, gravar, salvarStatus, confirmar, descrever = descreverPadrao,
  nomeUnidade = (u) => u || 'sem unidade',
}) {
  const acao = next === 'paga' ? 'marcada como paga' : 'desmarcada';
  const falha = (motivo) => ({ ok: false, aviso: `A folha de ${nome} NÃO foi ${acao}: ${semPontoFinal(motivo)}.` });

  // Desfaz o que ESTA operação acabou de criar (linhas nossas e intactas).
  // Devolve o complemento do aviso quando alguma não saiu.
  async function desfazerCriados(criados) {
    let ficaram = 0;
    for (const c of criados) {
      try { await remover(c.id); } catch { ficaram++; }
    }
    return ficaram
      ? ` — ATENÇÃO: ${ficaram} ${ficaram === 1 ? 'lançamento gerado agora não pôde ser retirado' : 'lançamentos gerados agora não puderam ser retirados'} de Contas a Pagar (a Folha substitui ao marcar como paga de novo)`
      : '';
  }

  // 1) O que já existe desta pessoa/mês — direto do BANCO.
  let existentes;
  try {
    existentes = await buscar(tag);
  } catch (err) {
    return falha(`não deu para consultar os lançamentos já gravados em Contas a Pagar (${errMsg(err)}). Nada foi alterado`);
  }
  const { intactas, editadas } = separarLancamentos(existentes, tag);

  // 2) Monta ANTES de qualquer escrita (erro aqui não deixa nada pela metade)
  //    — e antes da pergunta: com edição à mão, a decisão é POR UNIDADE.
  let novos = [];
  if (next === 'paga') {
    try {
      novos = montarNovos() || [];
    } catch (err) {
      return falha(`não deu para montar os lançamentos (${errMsg(err)}). Nada foi alterado`);
    }
    if (novos.some(mov => !String(mov?.description || '').includes(tag))) {
      return falha('lançamento montado sem a marca da folha (não daria para achar depois). Nada foi alterado');
    }
  }

  // 3) Editado à mão: nunca apaga e nunca gera por cima DAQUELA unidade. Quem
  //    decide é a pessoa; cancelar = nada muda.
  //    Paga (06/10/2026, revisão): a decisão é por UNIDADE de destino — a
  //    unidade que tem lançamento editado fica com ele; as outras partes do
  //    rateio são geradas normalmente. Antes uma parte editada (ex.: Itaim da
  //    Beatriz) fazia o "marcar paga" não gerar NENHUMA das outras unidades
  //    (Vila e Oscar ficavam sem os R$ 1.333 da folha).
  //    Se algum editado está numa unidade FORA do rateio (alguém trocou a
  //    unidade à mão), não dá pra saber que parte ele cobre: não gera nem
  //    apaga nada, e a pergunta diz quais unidades ficam sem lançamento.
  let apagar = intactas;
  if (editadas.length) {
    const lista = editadas.map(descrever).join('\n');
    const um = editadas.length === 1;
    let msg;
    if (next === 'paga') {
      const unidadeDe = (m) => m?.unit_id ?? m?.unitId ?? null;
      const destinos = [...new Set(novos.map(n => n.unitId))];
      const comEdicao = new Set(editadas.map(unidadeDe));
      const cabeca = `Já existe ${um ? 'lançamento desta folha EDITADO' : 'lançamentos desta folha EDITADOS'} À MÃO em Contas a Pagar:\n${lista}`;
      if (destinos.length && editadas.some(m => !destinos.includes(unidadeDe(m)))) {
        // Sem NENHUMA linha da tag (nem editada nem intacta) — o que fica descoberto.
        const comLinha = new Set([...editadas, ...intactas].map(unidadeDe));
        const descobertas = destinos.filter(u => !comLinha.has(u));
        novos = [];
        apagar = [];
        msg = `${cabeca}\n\n${um ? 'Ele está' : 'Algum deles está'} numa unidade fora do rateio desta folha, então NENHUM lançamento novo será gerado (o que está lá fica como está).`
          + (descobertas.length ? `\n\nATENÇÃO: ficam SEM lançamento desta folha: ${descobertas.map(nomeUnidade).join(', ')}. Lance à mão em Contas a Pagar.` : '')
          + `\n\nMarcar ${nome} como paga mesmo assim?`;
      } else {
        novos = novos.filter(n => !comEdicao.has(n.unitId));
        msg = `${cabeca}\n\n${um ? 'Ele fica como está' : 'Eles ficam como estão'} (não ${um ? 'é apagado' : 'são apagados'} nem ${um ? 'ganha' : 'ganham'} duplicata).`
          + (novos.length
            ? `\nSerá gerado só o que falta: ${novos.map(n => `${nomeUnidade(n.unitId)} R$ ${Number(n.amount || 0).toFixed(2).replace('.', ',')}`).join('; ')}.`
            : '\nNenhum lançamento novo será gerado.')
          + `\n\nMarcar ${nome} como paga?`;
      }
    } else {
      msg = `${um ? 'Este lançamento desta folha foi editado' : 'Estes lançamentos desta folha foram editados'} à mão em Contas a Pagar e NÃO ${um ? 'será apagado' : 'serão apagados'}:\n${lista}\n\nDesmarcar ${nome} mesmo assim? (Os lançamentos que a Folha gerou e ninguém mexeu serão retirados.)`;
    }
    if (!confirmar(msg)) return { ok: false, cancelado: true };
  }

  // 3b) Retira os gerados intactos (duplicata antiga inclusive — também a de
  //     uma unidade que ficou com o editado). Erro de exclusão ABORTA.
  let apagados = 0;
  for (const m of apagar) {
    try {
      await remover(m.id);
      apagados++;
    } catch (err) {
      return falha(`falhou ao retirar lançamento antigo de Contas a Pagar (${errMsg(err)})${apagados ? ` — ${apagados} de ${apagar.length} já ${apagados === 1 ? 'foi retirado' : 'foram retirados'}` : ''}. Clique de novo para terminar`);
    }
  }

  // 4) Gera os lançamentos novos (1 por unidade de destino).
  const criados = [];
  for (const mov of novos) {
    try {
      const salvo = await gravar(mov);
      if (!salvo?.id) throw new Error('o servidor não confirmou a gravação');
      criados.push(salvo);
    } catch (err) {
      const sobra = await desfazerCriados(criados);
      return falha(`falhou ao gravar em Contas a Pagar (${errMsg(err)})${sobra}`);
    }
  }

  // 5) Status por último (só com Contas a Pagar já certo).
  let salvo = false;
  try { salvo = !!(await salvarStatus()); } catch { salvo = false; }
  if (!salvo) {
    if (criados.length) {
      const sobra = await desfazerCriados(criados);
      return falha(`o status não foi salvo; os lançamentos gerados agora foram retirados de Contas a Pagar${sobra}`);
    }
    if (apagados) {
      return falha(`o status não foi salvo, mas ${apagados} ${apagados === 1 ? 'lançamento da folha já foi retirado' : 'lançamentos da folha já foram retirados'} de Contas a Pagar. Clique de novo para terminar`);
    }
    return falha('o status não foi salvo');
  }
  return { ok: true, criados: criados.length, apagados, editadosMantidos: editadas.length };
}

// ============================================================================
// FASE 1b (06/10/2026) — "marcar paga" pelo SERVIDOR: /api/fin op=folha-pagar
// ============================================================================
// O servidor grava a FOTOGRAFIA da linha (imutável por versão), um título por
// CANAL (transferência / cartão / espécie) pela origem_key
// 'folha:{professionalId|employeeId}:{competência}:{canal}' e por último o
// status do payroll_records — tudo numa transação. Duplo envio = mesma chave
// (nada duplica). A tela:
//   1. grava as edições pendentes da linha (saveRecord SEM status);
//   2. monta o pedido em CENTAVOS (montarPedidoFolhaPagar);
//   3. envia; 409 'conflitos' → modal (vincular / cancelar / manter) e
//      reenvia o MESMO corpo com uma resolução por conflito;
//   4. depois do ok publica o recibo da gerente (/api/folha-recibos), como antes.
// "Desmarcar" = reabrir COM MOTIVO (títulos da folha voltam para 'lançado';
// nada é apagado; o manual vinculado fica pago com a data dele; a data do
// pagamento fica guardada, menos quando o pagamento não saiu).
// Revisão 06/10/2026: sem data na tela, o servidor usa a data ORIGINAL desta
// folha (remarcar não muda o mês do desembolso); vincular manual de valor
// diferente do canal exige confirmação; manual vinculado a canal que zerou
// volta como conflito; falha de rede/tempo esgotado não afirma "não gravou".
// Testes: tests/folha-pagar-servidor.test.mjs.

export const CANAIS_FOLHA = ['transferencia', 'cartao', 'especie'];
export const ROTULO_CANAL = {
  transferencia: 'Transferência (PIX/TED)',
  cartao: 'Cartão benefício (VT/VR)',
  especie: 'Espécie (bônus)',
};
const fmtCents = (c) => `R$ ${(Number(c || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export { fmtCents as formatarCentavos };

// AAAA-MM
export const mesChave = (year, month) => `${year}-${pad2(month)}`;

// Linha da folha (o objeto de `rows` do FolhaPagamentoModule) → corpo do POST
// {op:'folha-pagar', acao:'pagar', …}. Tudo em centavos inteiros; o líquido é
// a soma EXATA dos canais. Lança Error (mensagem em português) quando a linha
// não pode ser paga assim (canal negativo, comissão negativa, sem funcionário).
//   year/month       = mês do PAGAMENTO (tela)   refYear/refMonth = competência
//   destinos         = destinosDaFolha(emp)      cargaOk = !motivoReciboBloqueado
// VALES (financeiro-013, 20/10/2026): com row.valesChave (a chave vales_folha do
// mês ligada, dita pelo servidor no op=vales-folha) o pedido leva SEMPRE
// componentes.vale (centavos, 0 sem vale) e vales [{vale_id, valor_cents}]; a
// Transferência = a de hoje SEM o vale (row.transferenciaSemVale, em REAIS; vira centavos aqui)
// − o vale, a mesma conta que o servidor refaz (alocarVales). Sem a chave, o
// pedido é exatamente o de antes (nem o campo vale vai).
export function montarPedidoFolhaPagar({
  row, year, month, refYear, refMonth, destinos = [], cargaOk = false, motivoBloqueio = null,
  motivo = null, resolucoes = [], dataPagamento = null, unidadePagadora = null,
}) {
  const emp = row?.emp;
  if (!emp?.id) throw new Error('linha da folha sem funcionário');
  const comVales = row.valesChave === true;
  const vales = comVales ? valesDoPedido(row.valesLinha) : [];
  const valeCents = vales.reduce((a, v) => a + v.valor_cents, 0);
  const canais = {
    transferencia: comVales
      ? paraCentavos(row.transferenciaSemVale ?? (Number(row.transferencia) + valeCents / 100)) - valeCents
      : paraCentavos(row.transferencia),
    cartao: paraCentavos(row.cartao),
    especie: paraCentavos(row.especie),
  };
  for (const c of CANAIS_FOLHA) {
    if (canais[c] < 0) {
      throw new Error(`o canal ${ROTULO_CANAL[c]} ficou negativo (${fmtCents(canais[c])}) — os descontos passam do que há para pagar nele; ajuste a linha antes de marcar`);
    }
  }
  const eff = row.effective || {};
  const componentes = {
    salario: paraCentavos(row.salarioEfetivo),
    comissao: paraCentavos(row.comissao),
    bonus: paraCentavos(row.especie),
    feriados: paraCentavos(eff.feriadosValor),
    vt: paraCentavos(row.vtVal),
    vr: paraCentavos(row.vrVal),
    ajudaCusto: paraCentavos(row.ajudaCusto),
    inss: paraCentavos(row.inssVal),
    descontoVt: paraCentavos(row.descVtVal),
    desconto: paraCentavos(eff.desconto),
    extras: paraCentavos(row.bancoExtras),
    faltas: paraCentavos(row.bancoDescontos),
  };
  if (componentes.comissao < 0) throw new Error('comissão negativa na linha');
  if (comVales) componentes.vale = valeCents;
  const corpo = {
    op: 'folha-pagar',
    acao: 'pagar',
    competencia: mesChave(refYear, refMonth),
    mesPagamento: mesChave(year, month),
    employeeId: emp.id,
    professionalId: emp.professionalId || null,
    nome: emp.name || 'funcionário',
    cargo: emp.cargo || null,
    contrato: emp.contractType || null,
    unitId: emp.unitId || '',
    destinos: [...new Set((Array.isArray(destinos) ? destinos : []).filter(Boolean).map(String))],
    cargaOk: cargaOk === true,
    motivoBloqueio: motivoBloqueio ? String(motivoBloqueio) : null,
    liquidoCents: canais.transferencia + canais.cartao + canais.especie,
    canais,
    componentes,
    resolucoes: Array.isArray(resolucoes) ? resolucoes : [],
  };
  if (comVales) corpo.vales = vales;
  if (motivo) corpo.motivo = String(motivo);
  // Opcionais: sem data, o servidor usa hoje (SP) e o título que JÁ está pago
  // mantém a data dele; sem unidade pagadora, a do funcionário (ou destinos[0]).
  if (dataPagamento) corpo.dataPagamento = String(dataPagamento);
  if (unidadePagadora) corpo.unidadePagadora = String(unidadePagadora);
  return corpo;
}

// Os vales da linha → a lista do pedido: inteiros > 0, sem repetir, por vale_id
// (lista estranha LANÇA: melhor não enviar do que mandar vale errado).
function valesDoPedido(lista) {
  const out = []; const vistos = new Set();
  for (const v of Array.isArray(lista) ? lista : []) {
    const id = Number(v?.vale_id); const c = Number(v?.valor_cents);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(c) || c < 0) throw new Error('lista de vales da linha inválida; atualize a tela');
    if (c === 0) continue;
    if (vistos.has(id)) throw new Error('vale repetido na linha; atualize a tela');
    vistos.add(id);
    out.push({ vale_id: id, valor_cents: c });
  }
  return out.sort((a, b) => a.vale_id - b.vale_id);
}

// Corpo do "reabrir" (= desmarcar). motivo: mínimo 3 caracteres.
// pagamentoNaoSaiu: o pagamento NÃO aconteceu → o servidor esquece a data
// guardada (o próximo "marcar paga" usa a data dele). Sem isso a data fica.
export function montarPedidoReabrir({ row, year, month, refYear, refMonth, motivo, pagamentoNaoSaiu = false }) {
  const emp = row?.emp;
  if (!emp?.id) throw new Error('linha da folha sem funcionário');
  const m = String(motivo || '').trim();
  if (m.length < 3) throw new Error('informe o motivo (mínimo 3 caracteres)');
  return {
    op: 'folha-pagar',
    acao: 'reabrir',
    competencia: mesChave(refYear, refMonth),
    mesPagamento: mesChave(year, month),
    employeeId: emp.id,
    professionalId: emp.professionalId || null,
    nome: emp.name || 'funcionário',
    motivo: m,
    ...(pagamentoNaoSaiu === true ? { pagamentoNaoSaiu: true } : {}),
  };
}

// Erro do /api/fin (FinApiErro: status, code, dados) → frase do aviso.
export function mensagemErroFolha(err, { nome = 'funcionário', acao = 'pagar' } = {}) {
  const feito = acao === 'reabrir' ? 'desmarcada' : 'marcada como paga';
  const d = err?.dados || {};
  // Sem resposta (rede) ou tempo esgotado: o servidor PODE ter gravado — não
  // dá para afirmar "não foi". Clicar de novo é seguro (mesma chave).
  if (err?.code === 'rede' || err?.code === 'http_504' || err?.status === 504) {
    const porque = err?.code === 'rede' ? 'sem resposta do servidor' : 'o servidor demorou demais (tempo esgotado)';
    return `Não deu para confirmar se a folha de ${nome} foi ${feito} (${porque}). Clique de novo: não duplica — se já tinha gravado, nada muda; depois recarregue a página para ver o status certo.`;
  }
  let motivo;
  switch (err?.code) {
    case 'sem_sessao': motivo = 'sua sessão expirou — entre de novo'; break;
    case 'sem_acesso': motivo = 'só o operador financeiro marca ou desmarca a folha'; break;
    case 'canais_nao_fecham':
      motivo = `transferência + cartão + espécie (${fmtCents(d.soma_canais_cents)}) não fecham com o líquido (${fmtCents(d.liquido_cents)}) — recarregue a página e confira a linha`;
      break;
    case 'componentes_nao_fecham':
      motivo = `os componentes do canal ${ROTULO_CANAL[d.canal] || d.canal || ''} somam ${fmtCents(d.componentes_cents)} e o canal ${fmtCents(d.canal_cents)} — recarregue a página e confira a linha`;
      break;
    // vales (financeiro-013, 20/10/2026): a tela relê os vales sozinha
    case 'vales_mudaram':
      motivo = 'os vales desta pessoa mudaram desde que a tela abriu. A linha foi atualizada: confira o vale embaixo do Desconto e marque de novo';
      break;
    case 'vales_desligados':
      motivo = 'os vales estão desligados para este mês de pagamento. A linha foi atualizada: confira e marque de novo';
      break;
    default:
      motivo = semPontoFinal(err?.message || String(err || 'erro desconhecido'));
  }
  return `A folha de ${nome} NÃO foi ${feito}: ${semPontoFinal(motivo)}.`;
}

// Envia o pedido. Devolve:
//   { ok: true, resposta }                         gravado (status já salvo)
//   { ok: false, conflitos, mensagem }             409 — a tela pergunta e reenvia
//   { ok: false, aviso, code }                     erro (nada gravado; code do servidor ou null)
export async function enviarPedidoFolha(corpo, { enviar, nome = 'funcionário' }) {
  const acao = corpo?.acao === 'reabrir' ? 'reabrir' : 'pagar';
  let r;
  try {
    r = await enviar(corpo);
  } catch (err) {
    if (acao === 'pagar' && err?.code === 'conflitos' && Array.isArray(err?.dados?.conflitos)) {
      return { ok: false, conflitos: err.dados.conflitos, mensagem: semPontoFinal(err.message) };
    }
    // code: a tela relê os vales quando o servidor diz que mudaram (20/10/2026)
    return { ok: false, aviso: mensagemErroFolha(err, { nome, acao }), code: err?.code || null };
  }
  if (!r || r.ok !== true) {
    return { ok: false, aviso: mensagemErroFolha(new Error('o servidor não confirmou a gravação'), { nome, acao }) };
  }
  if (r.status_salvo !== true) {
    // Não deveria acontecer (status vai na mesma transação) — mas não finge.
    return { ok: false, aviso: mensagemErroFolha(new Error('o servidor gravou os títulos mas não confirmou o status da folha; clique de novo (não duplica)'), { nome, acao }) };
  }
  return { ok: true, resposta: r };
}

// Escolha inicial de cada conflito pela sugestão do servidor:
//   'vincular' → vincular no canal sugerido (se o canal tiver valor e ainda
//                não tiver outro vinculado); 'cancelar' → cancelar; null → em branco.
// → { [id]: { acao: 'vincular'|'cancelar'|'manter'|'', canal, motivo } }
export function escolhasIniciais(conflitos, canais = {}) {
  const out = {};
  const usados = new Set();
  for (const c of Array.isArray(conflitos) ? conflitos : []) {
    let acao = '';
    let canal = c.canal_sugerido && CANAIS_FOLHA.includes(c.canal_sugerido) ? c.canal_sugerido : '';
    if (c.motivo === 'vinculado_canal_zerado') {
      // o canal dele zerou: sem escolha automática (cancelar um pagamento real é decisão humana)
    } else if (c.sugestao === 'vincular' && canal && Number(canais[canal]) > 0 && !usados.has(canal)) {
      acao = 'vincular';
      usados.add(canal);
    } else if (c.sugestao === 'cancelar') {
      acao = 'cancelar';
    }
    if (!canal || !(Number(canais[canal]) > 0)) canal = CANAIS_FOLHA.find(k => Number(canais[k]) > 0) || '';
    out[c.id] = { acao, canal, motivo: '', confirmaDiferenca: false };
  }
  return out;
}

// Vincular um manual cujo valor NÃO é o do canal: Contas a Pagar fica com o
// valor do manual e a diferença fica SEM título. → null (igual) ou
// { manual_cents, canal_cents, diferenca_cents } (diferença = canal − manual).
export function diferencaVinculo(conflito, canal, canais = {}) {
  const v = Number(conflito?.valor_cents) || 0;
  const c = Number(canais?.[canal]) || 0;
  if (!CANAIS_FOLHA.includes(canal) || !(c > 0) || v === c) return null;
  return { manual_cents: v, canal_cents: c, diferenca_cents: c - v };
}

// Confere as escolhas da tela antes de reenviar (as mesmas regras do servidor):
// uma resolução por conflito; vincular só em canal com valor; no máximo 1
// vínculo por canal. → { ok, erros: { id: msg }, resolucoes }
export function validarEscolhas(conflitos, escolhas, canais = {}) {
  const erros = {};
  const resolucoes = [];
  const porCanal = new Map();
  for (const c of Array.isArray(conflitos) ? conflitos : []) {
    const e = escolhas?.[c.id] || {};
    if (e.acao === 'vincular') {
      if (!CANAIS_FOLHA.includes(e.canal)) { erros[c.id] = 'Escolha o canal do vínculo.'; continue; }
      if (!(Number(canais[e.canal]) > 0)) { erros[c.id] = `O canal ${ROTULO_CANAL[e.canal]} está zerado nesta folha.`; continue; }
      if (porCanal.has(e.canal)) {
        erros[c.id] = `Só um lançamento por canal: ${ROTULO_CANAL[e.canal]} já está vinculado a outro.`;
        continue;
      }
      porCanal.set(e.canal, c.id);
      if (diferencaVinculo(c, e.canal, canais) && e.confirmaDiferenca !== true) {
        erros[c.id] = 'O valor deste lançamento é diferente do canal: confirme que é o mesmo pagamento (a diferença fica sem título) ou escolha outra opção.';
        continue;
      }
      resolucoes.push({ id: c.id, acao: 'vincular', canal: e.canal });
    } else if (e.acao === 'cancelar') {
      const motivo = String(e.motivo || '').trim();
      resolucoes.push(motivo ? { id: c.id, acao: 'cancelar', motivo } : { id: c.id, acao: 'cancelar' });
    } else if (e.acao === 'manter') {
      resolucoes.push({ id: c.id, acao: 'manter' });
    } else {
      erros[c.id] = 'Escolha o que fazer com este lançamento.';
    }
  }
  return { ok: Object.keys(erros).length === 0, erros, resolucoes };
}

// Por que o servidor achou o conflito (motivo do item) → texto.
export function motivoConflitoTexto(motivo) {
  switch (motivo) {
    case 'related_prof_id': return 'lançado para este profissional';
    case 'nome': return 'mesmo nome no favorecido/título';
    case 'primeiro_nome': return 'mesmo primeiro nome no favorecido/título';
    case 'folha_antiga': return 'lançamento da folha antiga (marca [FOLHA-…])';
    case 'vinculado_canal_zerado': return 'lançamento manual vinculado a um canal que agora está zerado (o sistema não o cancela sozinho)';
    default: return motivo ? String(motivo) : 'mesma pessoa e competência';
  }
}

// Resposta ok → frase curta para a faixa (só quando houve algo além do óbvio:
// conflito resolvido, título cancelado ou vinculado). null = nada a dizer.
// canais = os canais do pedido (centavos) — para avisar vínculo com valor diferente.
// hoje (AAAA-MM-DD) — a data usada só é dita quando não é a de hoje.
export function resumoPagamentoFolha(resp, { nome = 'funcionário', canais = null, hoje = null } = {}) {
  if (!resp || resp.ok !== true) return null;
  const tit = Array.isArray(resp.titulos) ? resp.titulos : [];
  const canc = Array.isArray(resp.cancelados) ? resp.cancelados : [];
  const desv = Array.isArray(resp.desvinculados) ? resp.desvinculados : [];
  const vinc = tit.filter(t => t.acao === 'vinculado' || t.vinculado);
  const mantidos = Array.isArray(resp.mantidos) ? resp.mantidos.length : 0;
  const dataOriginal = resp.data_pagamento_origem === 'original' && resp.data_pagamento && resp.data_pagamento !== hoje;
  const difs = canais ? vinc.filter(t => t.canal && Number(canais[t.canal]) > 0 && Number(t.valor_cents) !== Number(canais[t.canal])) : [];
  if (!canc.length && !vinc.length && !desv.length && !dataOriginal && !(resp.conflitos_resolvidos > 0)) return null;
  const partes = [];
  if (dataOriginal) partes.push(`pagamento mantido em ${dataBRCurta(resp.data_pagamento)} (a data original, não a de hoje)`);
  if (vinc.length) partes.push(`${vinc.length} lançamento(s) manual(is) vinculado(s) como pagamento`);
  for (const t of difs) {
    const c = Number(canais[t.canal]);
    partes.push(`o vinculado ao canal ${ROTULO_CANAL[t.canal] || t.canal} tem ${fmtCents(t.valor_cents)} e o canal ${fmtCents(c)} — Contas a Pagar ficou com ${fmtCents(t.valor_cents)} (diferença de ${fmtCents(c - Number(t.valor_cents))} sem título)`);
  }
  if (canc.length) partes.push(`${canc.length} lançamento(s) cancelado(s) (${canc.map(c => fmtCents(c.valor_cents)).join(', ')})`);
  if (desv.length) partes.push(`${desv.length} lançamento(s) manual(is) desvinculado(s) de canal zerado (continuam como estavam)`);
  if (mantidos) partes.push(`${mantidos} mantido(s) como outro pagamento (contam na DRE como "fora da fotografia"; a checagem (b) segue mostrando)`);
  return `Folha de ${nome} paga${resp.fotografia ? ` (fotografia v${resp.fotografia.versao})` : ''}: ${partes.join('; ') || 'conflitos resolvidos'}.`;
}
const dataBRCurta = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso || ''); };

// Resposta do "reabrir" → frase da faixa (null = nada além do óbvio).
export function resumoReabrirFolha(resp, { nome = 'funcionário' } = {}) {
  const tit = Array.isArray(resp?.titulos) ? resp.titulos : [];
  const reabertos = tit.filter(t => t.acao === 'reaberto').length;
  const manuais = tit.filter(t => t.acao === 'mantido_manual').length;
  const guardada = resp?.data_guardada !== false;
  if (!reabertos && !manuais) {
    return `Folha de ${nome} desmarcada (status pendente), mas nenhum título dela com a chave nova estava pago. Duas causas possíveis: ` +
      `(1) ela foi paga pelo caminho antigo (antes de 06/10/2026) — os lançamentos [FOLHA-…] continuam em Contas a Pagar e aparecem para vincular ou cancelar ao marcar paga de novo; ` +
      `(2) ela já tinha sido desmarcada (um clique repetido que chegou depois) — nada mais a fazer.`;
  }
  const partes = [];
  if (reabertos) partes.push(`${reabertos} título(s) da folha voltaram para lançado`);
  if (manuais) partes.push(`${manuais} lançamento(s) manual(is) vinculado(s) continuam pagos, com a data deles`);
  partes.push(guardada ? 'a data do pagamento ficou guardada para quando marcar paga de novo' : 'a data do pagamento foi descartada (o pagamento não saiu)');
  return `Folha de ${nome} desmarcada: ${partes.join('; ')}.`;
}
